import { Form } from '@ant-design/compatible';
import { useMutation } from '@apollo/client';
import { Button, Col, Icon, Input, InputNumber, message, Row, Spin } from 'antd';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferralRemove as ReferralRemoveIcon } from '~components/svgs';
import { MUTATION_BULK_OPERATION_FLOOR_PLANS } from '~pages/pms-dashboard/properties/listing-management/gallery-management/floor-plan/queries';

function FloorEditor({ id, floorPlans, form, onClose, onSuccess }) {
  const { t } = useTranslation();

  const [editingFloorPlans, setEditingFloorPlans] = useState(() => {
    if (floorPlans && floorPlans.length) {
      return floorPlans;
    } else {
      return [
        {
          block: '',
          floorIndex: undefined,
        },
      ];
    }
  });
  const [updateFloorPlansLoading, setUpdateFloorPlansLoading] = useState(false);

  const [updateFloorPlans] = useMutation(MUTATION_BULK_OPERATION_FLOOR_PLANS, {
    onError: () => {
      message.error(t('sorry_an_error_occurred_please_try_again'));
      setUpdateFloorPlansLoading(false);
    },
  });

  const buildFormData = (id, floorIndex, block) => {
    return id.map((currentId, index) => ({
      ...(currentId ? { id: currentId } : {}),
      floorIndex:
        typeof floorIndex[index] === 'number' ? floorIndex[index] : Number(floorIndex[index]),
      block: block[index],
    }));
  };

  const handleAudit = async () => {
    try {
      setUpdateFloorPlansLoading(true);
      form.validateFields(async (err, values) => {
        if (!err) {
          const existFormData = buildFormData(values.id, values.floorIndex, values.block);
          const mutationFloorPlansResponse = await updateFloorPlans({
            variables: {
              input: {
                propertyId: id,
                floorPlans: existFormData,
              },
            },
          });
          if (mutationFloorPlansResponse.data?.bulkOperateFloorPlans) {
            message.success(
              t('cms:cms.property.listing_management.floor_plan.modal.action.update.toast'),
            );
            setUpdateFloorPlansLoading(false);
            if (onSuccess) {
              onSuccess();
            }
            onClose();
          }
        } else {
          setUpdateFloorPlansLoading(false);
        }
      });
    } catch (error) {
      setUpdateFloorPlansLoading(false);
      message.error(error.toString());
    }
  };

  if (!editingFloorPlans) {
    return (
      <div className="payout-opps-viewer__spin">
        <Spin />
      </div>
    );
  }

  const handleAddFloor = () => {
    const newFormData = editingFloorPlans.concat({
      block: '',
      floorIndex: undefined,
    });
    setEditingFloorPlans(newFormData);
  };

  const deleteFloor = deleteIndex => {
    const newFormData = editingFloorPlans.filter(
      (_k, currentIndex) => currentIndex !== deleteIndex,
    );

    const ids = form.getFieldValue('id');
    const floorIndex = form.getFieldValue('floorIndex');
    const block = form.getFieldValue('block');
    form.setFieldsValue({
      id: ids.filter((_k, currentIndex) => currentIndex !== deleteIndex),
      floorIndex: floorIndex.filter((_k, currentIndex) => currentIndex !== deleteIndex),
      block: block.filter((_k, currentIndex) => currentIndex !== deleteIndex),
    });

    setEditingFloorPlans(newFormData);
  };

  return (
    <>
      <section className="floor-editor__section">
        <h3 className="floor-editor__body__title">
          {t('cms:cms.property.listing_management.floor_plan.modal.section.floor_settings')}
        </h3>

        <div className="floor-editor__form__header">
          <Row gutter={16}>
            <Col span={2} style={{ width: 48 }} />
            <Col span={11} style={{ width: 296 + 16 }}>
              <span>
                {t('cms:cms.property.listing_management.floor_plan.modal.field.floor_index')}:
              </span>
            </Col>
            <Col span={11} style={{ width: 296 + 16 }}>
              <span>{t('cms:cms.property.listing_management.floor_plan.modal.field.block')}:</span>
            </Col>
          </Row>
        </div>

        {editingFloorPlans.map((floor, index) => (
          <Row gutter={16} key={index}>
            <Col span={2} style={{ width: 48 }}>
              <Form.Item colon={false}>
                <span className="floor-editor__form__before-title">Floor</span>
              </Form.Item>
            </Col>
            <Col span={11} style={{ width: 296 + 16 }}>
              <Form.Item colon={false}>
                {form.getFieldDecorator(`floorIndex[${index}]`, {
                  initialValue: floor.floorIndex,
                  validateTrigger: 'onBlur',
                  rules: [
                    {
                      required: true,
                      message: t(
                        'cms:cms.property.listing_management.floor_plan.modal.error.floor_index.blank',
                      ),
                    },
                    {
                      validator: (_rule, value, callback) => {
                        if (value) {
                          const numValue = Number(value);
                          if (!Number.isInteger(numValue)) {
                            callback(
                              t(
                                'cms:cms.property.listing_management.floor_plan.modal.error.floor_index.not_int',
                              ),
                            );
                          } else {
                            callback();
                          }
                        } else {
                          callback();
                        }
                      },
                    },
                  ],
                })(
                  <InputNumber
                    placeholder={t(
                      'cms:cms.property.listing_management.floor_plan.modal.field.floor_index.placeholder',
                    )}
                    min={0}
                    autoComplete="off"
                  />,
                )}
              </Form.Item>
            </Col>
            <Col span={11} style={{ width: 296 + 16 }}>
              <Form.Item colon={false}>
                {form.getFieldDecorator(`block[${index}]`, {
                  rules: [
                    {
                      required: true,
                      message: t(
                        'cms:cms.property.listing_management.floor_plan.modal.error.block_name.blank',
                      ),
                    },
                    {
                      whitespace: true,
                      message: t('cms:cms.referral.commission_plan.modal.error.invalid'),
                    },
                    {
                      max: 20,
                      message: t(
                        'cms:cms.property.listing_management.floor_plan.modal.error.block_name.limit',
                      ),
                    },
                  ],
                  initialValue: floor.block,
                  validateTrigger: 'onBlur',
                })(
                  <Input
                    autoComplete="off"
                    placeholder={t(
                      'cms:cms.property.listing_management.floor_plan.modal.field.block.placeholder',
                    )}
                  />,
                )}
              </Form.Item>
            </Col>
            <Col span={2} style={{ width: 32, paddingRight: 0 }}>
              <If condition={editingFloorPlans.length > 1}>
                <Form.Item colon={false}>
                  <div
                    className="floor-editor__form__delete-icon"
                    onClick={() => deleteFloor(index)}
                  >
                    <ReferralRemoveIcon />
                  </div>
                </Form.Item>
              </If>
            </Col>
            {form.getFieldDecorator(`id[${index}]`, {
              initialValue: floor.id,
            })(<span />)}
          </Row>
        ))}

        <Row gutter={16}>
          <Col span={12} style={{ marginLeft: 48 }}>
            <span className="floor-editor__link" onClick={handleAddFloor}>
              Add New Floor
            </span>
          </Col>
        </Row>
      </section>

      <div className="floor-editor__footer">
        <Button
          ghost
          type="primary"
          size="large"
          className="floor-editor__cancel-button"
          onClick={onClose}
        >
          {t('cms:cms.property.listing_management.floor_plan.modal.btn.cancel')}
        </Button>

        <Button
          type="primary"
          size="large"
          disabled={updateFloorPlansLoading}
          className={classNames('floor-editor__save-button', {
            'floor-editor__save-button--loading': updateFloorPlansLoading,
          })}
          onClick={async () => {
            await handleAudit();
          }}
        >
          <If condition={updateFloorPlansLoading}>
            <Spin
              indicator={
                <Icon
                  className="floor-editor__button__icon"
                  type="loading"
                  style={{ fontSize: 14, color: 'white' }}
                  spin
                />
              }
            />
          </If>
          {t('cms:cms.property.listing_management.floor_plan.modal.btn.save')}
        </Button>
      </div>
    </>
  );
}

export default FloorEditor;
